import React, { useState } from 'react';
import { ResponsiveContainer } from 'recharts';
import ChartSkeleton from './ChartSkeleton';
import ChartEmpty from './ChartEmpty';

/**
 * ChartContainer — ResponsiveContainer wrapper with loading skeleton + empty state.
 * Wraps any Recharts chart component with consistent loading/empty/error handling.
 */
export default function ChartContainer({
  children,
  height = 300,
  loading = false,
  empty = false,
  filtered = true,
  entityName = 'data',
  chartType = 'area',
  onEmptyAction,
  onClick,
  style,
}) {
  const [hasError, setHasError] = useState(false);

  if (loading) {
    return <ChartSkeleton height={height} type={chartType} />;
  }

  if (empty) {
    return (
      <ChartEmpty
        height={height}
        filtered={filtered}
        entityName={entityName}
        onAction={onEmptyAction}
      />
    );
  }

  if (hasError) {
    return (
      <div style={{
        width: '100%',
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 3,
        background: 'var(--neutral-50)',
        border: '1px solid var(--neutral-200)',
      }}>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>
          Unable to load chart
        </p>
      </div>
    );
  }

  return (
    <div
      style={{ width: '100%', height, cursor: onClick ? 'pointer' : 'default', ...style }}
      onClick={onClick}
    >
      <ErrorBoundary onError={() => setHasError(true)}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </ErrorBoundary>
    </div>
  );
}

/**
 * Simple error boundary for chart rendering failures.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Chart render error:', error, info);
    if (this.props.onError) this.props.onError(error);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
